import React from 'react';
import { useSchool } from '../context/SchoolContext';
import { Download, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import './Financials.css';

const Financials = () => {
    const { payments = [], expenses = [], loadMorePayments, hasMorePayments, totalPaymentsCount, loadMoreExpenses, hasMoreExpenses, totalExpensesCount } = useSchool();

    // Calculate Summary
    const totalIncome = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const totalExpenses = expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
    const netProfit = totalIncome - totalExpenses;

    const downloadIncomeStatement = () => {
        // Prepare CSV Data
        const headers = ['Date', 'Type', 'Category/Source', 'Description', 'Amount', 'Method'];
        const rows = [];

        // Add Income
        payments.forEach(p => {
            rows.push([
                new Date(p.date || p.payment_date).toLocaleDateString(),
                'Income',
                'Student Payment',
                `Payment from ${p.customerId || p.customer_id}`,
                parseFloat(p.amount || 0).toFixed(2),
                p.method || p.payment_method
            ]);
        });

        // Add Expenses
        expenses.forEach(e => {
            rows.push([
                new Date(e.date || e.expense_date).toLocaleDateString(),
                'Expense',
                e.category || e.expense_category,
                e.description || '-',
                (-parseFloat(e.amount || 0)).toFixed(2), // Negative for expense
                e.method || e.payment_method
            ]);
        });

        // Convert to CSV
        const csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Income_Statement_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="page-container financials-page">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Financials</h2>
                    <p className="page-subtitle">Overview of financial performance</p>
                </div>
                <div className="header-actions">
                    <button className="btn btn-primary flex items-center gap-2" onClick={downloadIncomeStatement}>
                        <Download size={18} /> Download Income Statement
                    </button>
                </div>
            </div>

            {/* Financial Summary Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon income-icon">
                        <TrendingUp size={24} />
                    </div>
                    <div className="stat-info">
                        <h3>Total Income</h3>
                        <p className="stat-value text-green">${totalIncome.toFixed(2)}</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon expense-icon">
                        <TrendingDown size={24} />
                    </div>
                    <div className="stat-info">
                        <h3>Total Expenses</h3>
                        <p className="stat-value text-red">${totalExpenses.toFixed(2)}</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon profit-icon">
                        <DollarSign size={24} />
                    </div>
                    <div className="stat-info">
                        <h3>Net Profit</h3>
                        <p className={`stat-value ${netProfit >= 0 ? 'text-green' : 'text-red'}`}>
                            ${netProfit.toFixed(2)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Income Statement Preview (Optional/Simplified) */}
            <div className="card">
                <div className="card-header">
                    <h3>Recent Transactions</h3>
                </div>
                <div className="table-responsive">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Category</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                ...payments.map(p => ({ ...p, type: 'Income', formattedDate: p.date || p.payment_date, formattedAmount: parseFloat(p.amount || 0), cat: 'Student Payment' })),
                                ...expenses.map(e => ({ ...e, type: 'Expense', formattedDate: e.date || e.expense_date, formattedAmount: parseFloat(e.amount || 0), cat: e.category || e.expense_category }))
                            ]
                                .sort((a, b) => new Date(b.formattedDate) - new Date(a.formattedDate)) // Sort by date desc
                                .slice(0, 10) // Show last 10
                                .map(t => (
                                    <tr key={t.id}>
                                        <td>{new Date(t.formattedDate).toLocaleDateString()}</td>
                                        <td>
                                            <span className={`badge ${t.type === 'Income' ? 'badge-success' : 'badge-danger'}`}>
                                                {t.type}
                                            </span>
                                        </td>
                                        <td>{t.cat}</td>
                                        <td className={t.type === 'Income' ? 'text-green' : 'text-red'}>
                                            {t.type === 'Income' ? '+' : '-'}${t.formattedAmount.toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex justify-center gap-4 mt-8">
                {hasMorePayments && (
                    <button className="btn btn-secondary" onClick={loadMorePayments}>
                        Load More Income Data ({payments.length} of {totalPaymentsCount})
                    </button>
                )}
                {hasMoreExpenses && (
                    <button className="btn btn-secondary" onClick={loadMoreExpenses}>
                        Load More Expense Data ({expenses.length} of {totalExpensesCount})
                    </button>
                )}
            </div>
        </div>
    );
};

export default Financials;
